import { Box, Grid, GridItem } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import { shippingMethodItems } from '@/constants';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useTourContext } from '@/store/tourContext';
import { formatDate } from '@/utils';


const DemoOrderRequestForm = () => {
	const { registerElement } = useTourContext();
	const { isBVTutorialActive: isDemoBVTutorialActive } = useTutorialActive();

	// Mock data
	const mockPatient: SelectItem = {
		label: 'William Reed',
		value: 'mock-patient-id-1',
	};

	const mockProvider: SelectItem = {
		label: 'Dr. John Smith',
		value: 'mock-provider-id-1',
	};

	const mockClinic: SelectItem = {
		label: 'Demo Clinic',
		value: 'mock-clinic-id-1',
	};

	const mockProducts: SelectItem[] = [
		{
			label: 'Axolotl Dual Graft (Q4332)',
			value: 'mock-prior-auth-id-1',
		},
	];

	const mockProductSizes: SelectItem[] = [
		{ label: '2x2', value: 'mock-product-size-1' },
		{ label: '4x4', value: 'mock-product-size-2' },
		{ label: '6x6', value: 'mock-product-size-3' },
	];

	const mockShippingLocations: SelectItem[] = [
		{
			label: 'Main Office - 123 Main St, New York, NY 10001',
			value: 'mock-shipping-location-1',
		},
	];

	// Form state
	const [patient, setPatient] = useState<SelectItem | null>(mockPatient);
	const [provider, setProvider] = useState<SelectItem | null>(mockProvider);
	const [clinic, setClinic] = useState<SelectItem | null>(mockClinic);
	const [product, setProduct] = useState<SelectItem | null>(mockProducts[0]);
	const [productSize, setProductSize] = useState<string>('mock-product-size-1');
	const [productSizeLabel, setProductSizeLabel] = useState<string>('2x2');
	const [shipping, setShipping] = useState<SelectItem | null>({
		value: '953870000',
		label: 'First Overnight (8:30 am)',
	});
	const [shippingLocation, setShippingLocation] = useState<SelectItem | null>(
		mockShippingLocations[0],
	);
	const [dos, setDos] = useState<string>(formatDate(getTodayInNYTimezone()));
	const [quantity, setQuantity] = useState<string>('1');
	const [comments, setComments] = useState<string>('');

	// Refs for tour registration
	const shippingMethodRef = useRef<HTMLDivElement | null>(null);
	const orderProductRef = useRef<HTMLDivElement | null>(null);
	const orderSizeRef = useRef<HTMLDivElement | null>(null);
	const orderQuantityRef = useRef<HTMLDivElement | null>(null);

	// Register refs for tour
	useEffect(() => {
		if (!isDemoBVTutorialActive) {
			registerElement('BVShippingMethodRef', null);
			registerElement('BVOrderProductRef', null);
			registerElement('BVOrderSizeRef', null);
			registerElement('BVOrderQuantityRef', null);
			return;
		}

		let timeoutId: NodeJS.Timeout;
		
		// Use requestAnimationFrame to ensure DOM is ready, then setTimeout for additional delay
		const frameId = requestAnimationFrame(() => {
			timeoutId = setTimeout(() => {
				// Register Shipping Method - register the container for better highlighting
				if (shippingMethodRef.current) {
					registerElement('BVShippingMethodRef', shippingMethodRef.current);
				}

				// Register Order Product - register the container for better highlighting
				if (orderProductRef.current) {
					registerElement('BVOrderProductRef', orderProductRef.current);
				}

				// Register Order Size - register the container for better highlighting
				if (orderSizeRef.current) {
					registerElement('BVOrderSizeRef', orderSizeRef.current);
				}

				// Register Order Quantity - register the container for better highlighting
				if (orderQuantityRef.current) {
					registerElement('BVOrderQuantityRef', orderQuantityRef.current);
				}
			}, 300); // Delay to ensure elements are fully rendered
		});

		return () => {
			cancelAnimationFrame(frameId);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (isDemoBVTutorialActive) {
				registerElement('BVShippingMethodRef', null);
				registerElement('BVOrderProductRef', null);
				registerElement('BVOrderSizeRef', null);
				registerElement('BVOrderQuantityRef', null);
			}
		};
	}, [isDemoBVTutorialActive, registerElement]);

	const handleProductChange = (newValue: SingleValue<SelectItem>) => {
		if (newValue) {
			setProduct(newValue);
			// Auto-select first size when product changes
			if (mockProductSizes.length > 0) {
				setProductSize(String(mockProductSizes[0].value));
				setProductSizeLabel(mockProductSizes[0].label);
			}
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		console.log('[DemoOrderRequestForm] Form submitted with:', {
			patient,
			provider,
			clinic,
			product,
			productSize,
			shipping,
			shippingLocation,
			dos,
			quantity,
			comments,
		});
	};

	const isFormValid = useMemo(() => {
		return (
			!!patient?.value &&
			!!provider?.value &&
			!!clinic?.value &&
			!!product?.value &&
			!!productSize &&
			!!shipping?.value &&
			!!shippingLocation?.value &&
			!!dos &&
			!!quantity &&
			Number(quantity) > 0
		);
	}, [patient, provider, clinic, product, productSize, shipping, shippingLocation, dos, quantity]);

	return (
		<form onSubmit={handleSubmit}>
			<Box
				display="flex"
				justifyContent="center"
				px={{ base: 4, md: 6, lg: 2 }}
				py={{ base: 2, md: 4, lg: 2 }}
			>
				<Grid
					templateColumns={{
						base: '1fr',
						md: 'repeat(2, 1fr)',
						lg: '460px 280px',
					}}
					gap={{ base: 3, md: 4, lg: 3 }}
					columnGap={{ base: 0, md: 4 }}
					maxW={{ base: '100%', md: '700px', lg: '760px' }}
					w={{ base: '100%', lg: 'auto' }}
				>
					{/* Left Column */}
					<GridItem colSpan={{ base: 1, md: 2, lg: 1 }}>
						<Box
							display="flex"
							flexDirection="column"
							gap={{ base: 3, md: 4, lg: 2 }}
						>
							{/* Patient */}
							<FaceliftSelect<SelectItem, false>
								topLabel="Patient"
								placeholder="Type Patient Name"
								value={patient}
								onChange={(newValue) => setPatient(newValue)}
								items={[mockPatient]}
								isSearchable={true}
								isDisabled={true}
								required
							/>

							{/* Provider & Clinic Row */}
							<Grid
								templateColumns={{ base: '1fr', md: '1fr 1fr' }}
								gap={{ base: 3, md: 4, lg: 3 }}
							>
								<GridItem>
									<FaceliftSelect<SelectItem, false>
										topLabel="Provider"
										placeholder="Select Provider"
										value={provider}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) setProvider(newValue);
										}}
										items={[mockProvider]}
										isSearchable={true}
										isDisabled={true}
										required
									/>
								</GridItem>
								<GridItem>
									<FaceliftSelect<SelectItem, false>
										topLabel="Clinic"
										placeholder="Select Clinic"
										value={clinic}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) setClinic(newValue);
										}}
										items={[mockClinic]}
										isSearchable={false}
										isDisabled={true}
										required
									/>
								</GridItem>
							</Grid>

							{/* Product Selection */}
							<Box ref={orderProductRef}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Product Selection"
									placeholder="Type Product Name"
									value={product}
									onChange={handleProductChange}
									items={mockProducts}
									isSearchable={true}
									isDisabled={false}
									required
								/>
							</Box>

							{/* Size */}
							<Box ref={orderSizeRef}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Size"
									placeholder="Select Size"
									value={
										mockProductSizes.find((s) => s.value === productSize) || null
									}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setProductSize(String(newValue.value));
											setProductSizeLabel(String(newValue.label));
										}
									}}
									items={mockProductSizes}
									isSearchable={true}
									isDisabled={false}
									required
								/>
							</Box>
						</Box>
					</GridItem>

					{/* Right Column */}
					<GridItem colSpan={{ base: 1, md: 2, lg: 1 }}>
						<Box
							display="flex"
							flexDirection="column"
							gap={{ base: 3, md: 4, lg: 2 }}
						>
							{/* DOS and Shipping Location */}
							<Grid
								templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr' }}
								gap={{ base: 3, md: 4, lg: 2 }}
							>
								<GridItem>
									<FaceliftDatePicker
										topLabel="DOS"
										value={dos}
										onChange={(date: string) => setDos(date)}
										placeholder="MM/DD/YYYY"
										disabled={false}
										minDate={getTodayInNYTimezone()}
										defaultToToday
										required
									/>
								</GridItem>
								<GridItem>
									<FaceliftSelect<SelectItem, false>
										topLabel="Shipping Location"
										placeholder="Select Location"
										value={shippingLocation}
										onChange={(newValue: SingleValue<SelectItem>) => {
											if (newValue) setShippingLocation(newValue);
										}}
										items={mockShippingLocations}
										isSearchable={true}
										isDisabled={true}
										required
									/>
								</GridItem>
							</Grid>

							{/* Shipping Method and Quantity */}
							<Grid
								templateColumns={{ base: '1fr', md: '1fr 1fr', lg: '1fr' }}
								gap={{ base: 3, md: 4, lg: 2 }}
							>
								<GridItem>
									<Box ref={shippingMethodRef}>
										<FaceliftSelect<SelectItem, false>
											topLabel="Shipping Method"
											placeholder="Select"
											value={shipping}
											onChange={(newValue: SingleValue<SelectItem>) => {
												if (newValue) setShipping(newValue);
											}}
											items={shippingMethodItems}
											isSearchable={false}
											isDisabled={false}
										/>
									</Box>
								</GridItem>
								<GridItem>
									<Box ref={orderQuantityRef}>
										<FaceliftInput
											topLabel="Quantity"
											placeholder="-"
											type="number"
											value={quantity}
											onChange={(e) => setQuantity(e.target.value)}
											required
										/>
									</Box>
								</GridItem>
							</Grid>
						</Box>
					</GridItem>

					{/* Comments - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<FaceliftInput
							topLabel="Comments"
							placeholder="Type Here"
							value={comments}
							onChange={(e) => setComments(e.target.value)}
						/>
					</GridItem>

					{/* Buttons - Full Width */}
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<Box
							display="flex"
							flexDirection={{ base: 'column', sm: 'row' }}
							gap={3}
							justifyContent={{ base: 'center', lg: 'flex-end' }}
							mt={{ base: 0, lg: 1 }}
						>
							<FaceliftButton
								minW={{ base: '100%', sm: '160px' }}
								type="submit"
								cursor="pointer"
								disabled={!isFormValid}
							>
								Submit
							</FaceliftButton>
						</Box>
					</GridItem>
				</Grid>
			</Box>
		</form>
	);
};

export default DemoOrderRequestForm;

